import type { FeedsStatus } from '@ofmos/shared/feeds';
import type { FulfillmentConfiguration } from '@ofmos/shared/fulfillment-configuration';
import { DISPLAY_LOCALE, formatRelative } from '../../../fields/format';
import { FULFILLMENT } from './configuration';

/**
 * HOW OLD THE DATA UNDER A PAGE IS (redesign register D9, R48, R-K1 "stale-data notes in the
 * orange late tone"; brief FE-FOUNDATION section 11). Pure, so every rule below is a test.
 *
 * The lines, in the mockups' words:
 *  - `telegram`: "Telegram read <relative>" from `touch.newestMessageAt`, the newest message the
 *    last run saw (Dashboard; decision page item 10.3 answered B: a run counts as working while
 *    that message is under 48 hours old, so the run's own time would hide how old the data is);
 *  - `contact`: "Last contact from Telegram, read <relative>" from the same instant (Clients,
 *    clients-v2.html:157);
 *  - `intakes`: "Intakes checked <relative>" from the inbound sources' `lastPullAt`;
 *  - `calls`: "Calls synced <relative>" from the Fathom accounts' `lastSuccessAt` (client page).
 * A source that never ran leaves its line out, as the Dashboard's `Freshness` line does; so does
 * Telegram while its newest run has not read a message (`newestMessageAt` null).
 *
 * STALE (D9): Telegram when its last success is older than the configuration's threshold (12
 * hours in OFMOS, `telegramStaleHours`) or its last run failed, measured from the run's time as
 * the stale notice's "since HH:MM" is (item 10.3 moves only the line's age);
 * the intakes when a source's last pull failed (`lastPullError`) or the oldest last pull is older
 * than the configuration's threshold (48 hours in OFMOS, `intakesStaleHours`; Q-FND-7, decision
 * page item 10.2 answered B: the pull runs once a day, so two days without one is stale even
 * without an error). A stale line takes the late tone.
 *
 * SEVERAL SOURCES, ONE LINE (open question Q-FND-6, decided A): the OLDEST sets the age, because
 * an honest data age is the worst one. A source that never ran has no age to compare and is
 * left out of the comparison.
 */
export type DataAgeSource = 'telegram' | 'contact' | 'intakes' | 'calls';

export interface DataAge {
  readonly source: DataAgeSource;
  readonly text: string;
  readonly stale: boolean;
  /** The instant the age is measured from. */
  readonly at: string;
}

/** D9's threshold in milliseconds: H8's "threshold the configuration sets". */
export function telegramStaleMs(config: FulfillmentConfiguration = FULFILLMENT): number {
  return config.telegramStaleHours * 3_600_000;
}

/** This build's threshold: 12 hours in OFMOS. */
export const TELEGRAM_STALE_MS = telegramStaleMs();

/** Decision page item 10.2's threshold in milliseconds, from the configuration. */
export function intakesStaleMs(config: FulfillmentConfiguration = FULFILLMENT): number {
  return config.intakesStaleHours * 3_600_000;
}

const LEAD: Readonly<Record<DataAgeSource, string>> = {
  telegram: 'Telegram read',
  contact: 'Last contact from Telegram, read',
  intakes: 'Intakes checked',
  calls: 'Calls synced',
};

function oldest(values: readonly (string | null)[]): string | null {
  let pick: string | null = null;
  for (const value of values) {
    if (value === null) continue;
    if (pick === null || Date.parse(value) < Date.parse(pick)) pick = value;
  }
  return pick;
}

/** Stale past the configured hours without a successful run, or when the last run failed. */
export function isTelegramStale(
  status: FeedsStatus,
  now: Date,
  config: FulfillmentConfiguration = FULFILLMENT,
): boolean {
  const touch = status.touch;
  if (touch.lastRunOk === false) return true;
  if (touch.lastSuccessAt === null) return false;
  return now.getTime() - Date.parse(touch.lastSuccessAt) > telegramStaleMs(config);
}

export function dataAgeOf(
  source: DataAgeSource,
  status: FeedsStatus,
  now: Date,
  config: FulfillmentConfiguration = FULFILLMENT,
): DataAge | null {
  let at: string | null;
  let stale: boolean;
  switch (source) {
    case 'telegram':
    case 'contact':
      at = status.touch.newestMessageAt;
      stale = isTelegramStale(status, now, config);
      break;
    case 'intakes':
      at = oldest(status.intake.map((entry) => entry.lastPullAt));
      stale =
        status.intake.some((entry) => entry.lastPullError !== null) ||
        (at !== null && now.getTime() - Date.parse(at) > intakesStaleMs(config));
      break;
    case 'calls':
      at = oldest(status.fathom.map((entry) => entry.lastSuccessAt));
      stale = false;
      break;
  }
  if (at === null) return null;
  return { source, at, stale, text: `${LEAD[source]} ${formatRelative(at, now)}` };
}

/** "05:12": the time of day in the person's own clock, 24 hours, as the components page writes it. */
export function clockTime(iso: string): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/**
 * THE STALE TELEGRAM SENTENCE (components page section 9, dashboard-v2-side.html:189), or null
 * when Telegram is fresh or never succeeded: "Telegram has not been read since 05:12, 13 hours
 * ago. Colours and days may be out of date."
 */
export function staleTelegramSentence(status: FeedsStatus, now: Date): string | null {
  const at = status.touch.lastSuccessAt;
  if (at === null || !isTelegramStale(status, now)) return null;
  return `Telegram has not been read since ${clockTime(at)}, ${formatRelative(at, now)}. Colours and days may be out of date.`;
}
